import React from 'react';

// Types de base pour les langues
export type LanguageCode = 'wf' | 'ba' | 'la'| 'ff'| 'co'| 'sw' | 'ka' | 'yo';

// Type de base pour les mots
export interface Word {
  original: string;
  translation: string;
  translationEn?: string;
  audio?: string;
  illustration?: string;
  explanation?: string;
  example?: string;
}

export interface LearnedSentence {
  original: string;
  french: string;
  audio?: string;
  mastered: boolean;
  category: string;
  timestamp: number;
  words: string[];        // Ajout de la compatibilité 
  translation?: string;  // Ajout de la compatibilité
  type?: 'sentence';
  interval: number; 
    attempts: number;
  firstAttemptCorrect: boolean;
}

// Nouvelle interface pour les catégories de phrases
export interface SentenceCategory {
  icon: string;
  description: string;
}

export interface SentenceCategoryDictionary {
  [category: string]: SentenceCategory;
}

export interface RevisionSessionInfo {
  category: string;
  words: string[];
  scores: Record<string, number>;
  timestamp: number;
  learnedWords: string[];
  mode: 'preview' | 'test' | 'summary';
  currentIndex: number;
}

export interface SentenceForReview {
  original: string;
  french: string;
  audio?: string;
  category: string;
  type: 'word' | 'sentence' | 'grammar';
  mastered: boolean;
  timestamp: number;
}

export interface LastWordInfo {
  word: string;
  category: string;
  isCorrect: boolean;
  timestamp: number;
  type: 'word' | 'sentence' | 'grammar';
  french?: string;
  audio?: string;
  nextReview?: number;  
  interval?: number;    
  easeFactor?: number;  
  words?: string[];     
}

export interface LastSentenceInfo {
  original: string;
  french: string;
  category: string;
  isCorrect: boolean;
  timestamp: number;
  nextReview: number;
  audio?: string;
  words: string[];
}

export interface FinishSessionOptions {
  showStats?: boolean;
  lastSentenceInfo?: LastSentenceInfo;
  lastWordInfo?: LastWordInfo;
}

export interface RevisionProgress {
  wordsToReview: Set<string>;
  revisionProgress: Record<string, WordProgress>;
  recentlyReviewed: LearnedWord[];
  language: LanguageCode;
   sessionHistory?: RevisionWordInfo[];
}

export interface WolofWord extends Word {
  audio?: string;
  illustration?: string;
  explanation?: string;
  example?: string;
}

export interface LingalaWord extends Word {
  audio?: string;
  illustration?: string;
  explanation?: string;
  example?: string;
}

// Types pour les données de langue et catégories
export interface WordData {
  translation: string;
  // English equivalents of translation/explanation/example — optional and
  // absent on virtually all entries today. When missing, display code must
  // fall back to the French field (see src/utils/localizedContent.ts) so
  // leaving these unset is always safe and produces no visible change.
  translationEn?: string;
  audio?: string;
  fr?: string;
  illustration?: string;
  explanation?: string;
  explanationEn?: string;
  example?: string;
  exampleEn?: string;
  type?: 'word' | 'sentence' | 'grammar';
  displayName?: string;
  reading?: string;
  // English translation of `reading` — optional, filled in progressively.
  // Falls back to `reading` when missing (see src/utils/localizedContent.ts).
  readingEn?: string;
  leadSign?: string;
  // Court résumé (1-2 phrases) de la correspondance en langues africaines
  // portée par leadSign — affiché en infobulle au survol du glyphe mis en
  // évidence, sans avoir à ouvrir toute la lecture analytique.
  leadSignNote?: string;
  // Kama uniquement (signes unilitères de « Lire les hiéroglyphes ») : un mot
  // réel, déjà présent dans une autre catégorie Kama, qui contient ce signe —
  // affiché dans l'onglet « Un mot » de HieroglyphCard. `category` est la clé
  // du CategoryDictionary Kama (ex. 'Objets sacrés et du quotidien').
  signExample?: { key: string; category: string };
  // Kama uniquement : transforme la fiche en QCM (HieroglyphCard masque
  // translittération/traduction tant que l'utilisateur n'a pas répondu).
  quiz?: {
    question: string;
    options: string[];
    correctIndex: number;
    explanation: string;
    // True when `options` are themselves hieroglyph strings to compare (e.g.
    // "which of these four sign orderings is correct?"), not French prose —
    // renders them with the hieroglyph font at reading size instead of the
    // small default button text.
    optionsAreGlyphs?: boolean;
    // English siblings of question/options/explanation — optional, filled
    // in progressively. Falls back to the French fields when missing (see
    // src/utils/localizedContent.ts). `optionsEn` is only meaningful when
    // `optionsAreGlyphs` is falsy (glyph options need no translation).
    questionEn?: string;
    optionsEn?: string[];
    explanationEn?: string;
  };
  // Kama uniquement : marque une fiche comme titre de section plutôt que
  // mot à retenir — rendu sans cadre ni bouton « Détails » par
  // HieroglyphCard, et sert de point d'ancrage au repère « Chapitre X/N »
  // affiché par KamaLearnMode pour les fiches qui suivent.
  isSectionHeader?: boolean;
  // Kama uniquement : mini-quiz de fin de chapitre (« Révision : … ») dans
  // Lire les hiéroglyphes — ni un mot à retenir, ni une fiche du chapitre :
  // KamaLearnMode l'exclut du compteur « Fiche X/N » et des signes voisins
  // gravés autour de la stèle.
  isChapterReview?: boolean;
  // Kama uniquement : le nom (royal) doit être encadré d'un cartouche à
  // l'affichage — réservé aux noms propres de souverains, seuls noms que
  // les scribes égyptiens entouraient réellement de cet anneau ovale
  // (voir la fiche « Cartouche » dans Lire les hiéroglyphes).
  cartouche?: boolean;
  // Kama uniquement : photographie d'un monument (temple, stèle, objet)
  // affichée à la place des glyphes dans HieroglyphCard — fiches du nœud
  // « Sur les murs des temples ». `credit` doit rester visible : les
  // licences CC BY / CC BY-SA l'exigent.
  photo?: {
    src: string;
    title: string;
    titleEn?: string;
    caption: string;
    captionEn?: string;
    credit: string;
    sourceUrl: string;
  };
  // Kama uniquement : leçon guidée par la mascotte (l'ibis) — rendue par
  // KamaGuideCard à la place de la fiche habituelle. L'ibis explique la
  // règle bulle par bulle pendant que la phrase, découpée mot à mot,
  // allume les mots dont il parle. Remplace l'ancien trio
  // phrase / analyse (long texte) / second exemple de « Phrase ».
  guide?: KamaGuide;
}

export interface KamaGuideWord {
  glyphs: string;
  translit: string;
  gloss: string;
  glossEn: string;
}

export interface KamaGuideSentence {
  words: KamaGuideWord[];
  translation: string;
  translationEn: string;
}

export interface KamaGuide {
  sentences: KamaGuideSentence[];
  steps: Array<{
    text: string;
    textEn: string;
    // Index into `sentences` shown during this step (default: the previous
    // step's, or 0) — lets the last bubble switch to the second example.
    sentence?: number;
    // Indices into that sentence's `words` to light up; none = all plain.
    highlight?: number[];
  }>;
}

export interface CategoryData {
  [word: string]: WordData;
}

export interface CategoryDictionary {
  [category: string]: CategoryData | GrammarCategory;
}

export interface LanguageData {
  code: LanguageCode;
  name: string;
  nativeName: string;
  categories: CategoryDictionary;
  categoryIllustrations: { [key: string]: string };
  sentencesToConstruct: Sentence[];
  sentenceCategories: SentenceCategoryDictionary;
  sentencesGap?: RevisionSentenceCollection;
}

// ✅ MODIFICATION: Types pour les données utilisateur - LearnedWord étendu
export interface LearnedWord {
  word: string;
  category: string;
  subcategory?: string;
  language: LanguageCode;
  timestamp?: number;
  // ✅ AJOUT: Nouvelles propriétés optionnelles
  audio?: string;
  translation?: string;
  translationEn?: string;
  explanation?: string;
  example?: string;
}

export interface WordProgress {
  correct: number;
  incorrect: number;
  tested: number;
  lastReviewed: number;
  nextReview: number;
  interval: number;
  easeFactor: number;
  language: LanguageCode;
  category?: string;
}

export interface SerializedUserProgress {
  learnedWords: string[];
  wordsToReview: string[];
  wordProgress: {
    [key: string]: WordProgress;
  };
  recentlyLearnedWords: LearnedWord[];
  language: LanguageCode;
  timestamp?: number;
}

export interface UserProgress {
  learnedWords: Set<string>;
  wordsToReview: Set<string>;
  wordProgress: {
    [key: string]: WordProgress;
  };
  recentlyLearnedWords: LearnedWord[];
  language: LanguageCode;
}

// ✅ MODIFICATION: Props des composants avec saveProgressOnSummary
export interface LanguageSelectorProps {
  value: LanguageCode;
  onChange: (value: LanguageCode) => void;
}

export interface LearnModeProps {
  languageCode: LanguageCode;
  category: string;
  subcategory?: string;
  onBackToCategories: () => void;
  onGameComplete: () => void;
  onWordLearned: (word: string, correct: boolean) => void;
  onWordsLearned: (words: LearnedWord[]) => void;
  resetToken?: number;
  words?: Record<string, Record<string, WordData>>;
  onAnswer?: (correct: boolean) => void;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
  wordRange?: { start: number; end: number };
  onNextCategory?: () => void;
}

export interface MainMenuProps {
  languageCode: LanguageCode;
  onSelectMode: (mode: AppMode) => void;
  reviewDue: boolean;
  onResetProgress: () => void;
  learnedWordsCount: number;
  onLanguageChange: (lang: LanguageCode) => void;
}

export interface CategorySelectionProps {
  languageCode: LanguageCode;
  categories: CategoryDictionary;
  categoryIllustrations: { [key: string]: string };
  onSelectCategory: (category: string) => void;
  completedCategories: string[];
}

export interface RevisionSentence {
  original: string;
  french: string;
  english?: string;
  audio?: string;
  testWord?: string;
  difficulty?: 1 | 2 | 3; // 1=facile, 2=moyen, 3=difficile
}

export interface RevisionSentenceCollection {
  [word: string]: RevisionSentence[];
}

// Nouvelle interface pour la sélection de catégories de phrases
export interface SentenceCategorySelectionProps {
  languageCode: LanguageCode;
  categories: SentenceCategoryDictionary;
  onSelectCategory: (category: string) => void;
  onBack: () => void;
}

// ✅ MODIFICATION: RevisionModeProps avec saveProgressOnSummary
export interface RevisionModeProps {
  languageCode: LanguageCode;
  onBackToMenu: () => void;
  onWordRevised: (word: string, isCorrect: boolean) => void;
  onUpdateRevisionState?: (state: any) => void;
  checkDailyLimit?: (limitType: 'dailyRevisions' | 'exercisesPerDay') => boolean;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
}

// ✅ MODIFICATION: QuizComponentProps avec saveProgressOnSummary
export interface QuizComponentProps {
  languageCode: LanguageCode;
  words: Record<string, Word[]>;
  onComplete: (score: number) => void;
  onAnswerSubmit?: (correct: boolean) => void;
  backgroundAudioUrl: string;
  correctAnswerSoundUrl: string;
  wrongAnswerSoundUrl: string;
  onGameFinished?: () => void;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
}

// ✅ CORRECTION: SentenceConstructionGameProps avec userProgress requis
export interface SentenceConstructionGameProps {
  languageCode: LanguageCode;
  onBackToCategories: () => void;
  onGameComplete: () => void;
  onAnswer?: (correct: boolean) => void;
  onSentencesLearned?: (sentences: LearnedSentence[]) => void;
  isMobileView?: boolean;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
  userProgress: UserProgress;
  filterCategories?: string[];
  onCategoryComplete?: (category: string) => void;
  onSentierMode?: (isSentier: boolean) => void;
  onShare?: () => void;
}

export type SentenceConstructionMode = 'selection' | 'category-details' | 'preview' | 'test' | 'summary' | 'category-selection'| 
'revision';

// ✅ MODIFICATION: SentenceGapGameProps avec saveProgressOnSummary
export interface SentenceGapGameProps {
  languageCode: LanguageCode;
  onBackToCategories: () => void;
  onGameComplete: () => void;
  onAnswer?: (correct: boolean) => void;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
  onShare?: () => void;
}

// ✅ AJOUT: Nouveau type pour ExerciseModeProps
export interface ExerciseModeProps {
  languageCode: LanguageCode;
  onBackToCategories: () => void;
  onExerciseComplete: () => void;
  checkDailyLimit?: (limitType: 'dailyRevisions' | 'exercisesPerDay') => boolean;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
  onShare?: () => void;
}

// Types pour la révision
export interface RevisionWordInfo {
  word: string;
  category: string;
  isCorrect?: boolean;
  timestamp: number;
  nextReview?: number;
  interval?: number;    
  easeFactor?: number;
  translation?: string;
  audio?: string;
  isSentence?: boolean;
  grammarType?: 'rule' | 'conjugation' | 'vocabulary';
  subCategory?: string;
}

export interface GrammarWordInfo extends RevisionWordInfo {
  grammarType: 'rule' | 'conjugation' | 'vocabulary';
  subCategory?: string;
}

export interface SaveDataInput {
  progress: SerializedUserProgress;
  completedCategories: string[];
  type?: string;  
  timestamp?: number;
  _metadata?: {  
    type: string;
    grammarProgress?: any;
    sentenceProgress?: any;
    timestamp: number;
    lastUpdated: string;
    [key: string]: any;
  };
  exercises?: {
    [exerciseType: string]: {
      progress: any;
      timestamp: number;
      lastUpdated: string;
      [key: string]: any;
    };
  };
}

export interface DriveResponse {
  progress: SerializedUserProgress;
  completedCategories: string[];
  type?: string;  
  timestamp?: number;  
  _metadata?: {  
    type: string;  
    grammarProgress?: any;  
    sentenceProgress?: any;  
    timestamp: number;  
    lastUpdated: string;  
    [key: string]: any;  
  };
  exercises?: {
    [exerciseType: string]: {
      progress: any;
      timestamp: number;
      lastUpdated: string;
      [key: string]: any;
    };
  };
}

// ✅ MODIFICATION: GrammarModeProps avec saveProgressOnSummary
export interface GrammarModeProps {
  languageCode: LanguageCode;
  onBackToCategories: () => void;
  onCategoryComplete: (category: string) => void;
  onWordsLearned?: (words: LearnedWord[]) => void;
  resetToken: number;
  isLoading?: boolean;
  isMobileView?: boolean;
  onSessionComplete?: () => void;
  saveProgressOnSummary?: (newProgress: UserProgress, context?: any) => Promise<void>;
  userProgress?: UserProgress;
  grammarUnit?: number;
  grammarSubcategoryFilter?: string[];
  onAnswerSubmit?: (correct: boolean) => void;
  onStartRevision?: () => void;
  onFilterComplete?: () => void;
  onShare?: () => void;
}

// Types pour la grammaire
export interface GrammarItem {
  translation?: string;
  translationEn?: string;
  fr?: string;
  explanation?: string;
  explanationEn?: string;
  example?: string;
  exampleEn?: string;
  examples?: string[];
  examplesEn?: string[];
  desc?: string;
  audio?: string;
}

export interface GrammarSubcategory {
  [key: string]: GrammarItem;
}

export interface GrammarCategory {
  [subcategory: string]: GrammarSubcategory;
}

export interface GrammarStructure {
  [category: string]: GrammarCategory;
}

// Types pour les phrases
export interface Sentence {
  original: string;
  french: string;
  // English translation of `french` — optional, absent today. Falls back to
  // `french` when missing (see src/utils/localizedContent.ts).
  english?: string;
  words: string[];
  audio?: string;
  category?: string;
  tags?: string[];
  // Kama only: the sentence's actual hieroglyphic writing (Gardiner signs,
  // verified glyph-by-glyph — see the kama-hieroglyph-decoder skill).
  // Optional and unused by every other language's Sentence data.
  hieroglyphs?: string;
  // Kama only: per-word breakdown of `hieroglyphs`, parallel to `words`
  // (same length/order) — lets SentenceConstructionGame's tiles display the
  // glyph for that specific word instead of its transliteration.
  hieroglyphWords?: string[];
}

// Configuration et paramètres
export interface RevisionSettings {
  initialInterval: number;
  easeFactor: number;
  minimumInterval: number;
  maximumInterval: number;
}

export interface ResetProgressOptions {
  setCompletedCategories: (categories: string[]) => void;
  setUserProgress: (progress: UserProgress) => void;
  setQuizScore: (score: number) => void;
  user: any | null;
  saveDataToGoogleDrive: () => Promise<void>;
  currentLanguage: LanguageCode;
}

export type AppMode = 'menu' | 'learn' | 'review' | 'quiz' | 'sentenceConstruction' | 'sentenceGap' | 'grammar'| 'exercise'| 'progression' | 'profile' | 'challenge' | 'minigames' | 'dialogue' | 'songAnalyzer' | 'wordle' | 'memoryDuel' | 'forge' | 'weaver';
export type DifficultyLevel = 'easy' | 'medium' | 'hard';

export interface ProgressPieProps {
  progress: number;
  size?: number;
  className?: string;
}

export interface AnimatePresenceProps {
  children?: React.ReactNode;
  initial?: boolean;
  custom?: any;
  onExitComplete?: () => void;
  exitBeforeEnter?: boolean;
  presenceAffectsLayout?: boolean;
  mode?: "sync" | "wait" | "popLayout";
}

// ✅ AJOUT: Interface pour RevisionStatsModal
export interface RevisionStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  wordDataMap: Record<string, Record<string, WordData>>;
  revisionHistory: RevisionWordInfo[];
  wordProgress?: Record<string, any>;
  isMobileView?: boolean;
}

// Fonctions de sérialisation
export const serializeUserProgress = (progress: UserProgress): SerializedUserProgress => ({
  learnedWords: Array.from(progress.learnedWords),
  wordsToReview: Array.from(progress.wordsToReview),
  wordProgress: progress.wordProgress,
  recentlyLearnedWords: progress.recentlyLearnedWords,
  language: progress.language
});

// ✅ FONCTION CORRIGÉE: Support snake_case ET camelCase + Protection anti-boucle
export const deserializeUserProgress = (serialized: any): UserProgress => {
  // Support à la fois snake_case (ancien format localStorage/Supabase)
  // et camelCase (nouveau format standard TypeScript)
  
  if (!serialized) {
    return {
      learnedWords: new Set<string>(),
      wordsToReview: new Set<string>(),
      wordProgress: {},
      recentlyLearnedWords: [],
      language: 'ba' as LanguageCode
    };
  }

  // ✅ PROTECTION: Vérifier si déjà désérialisé (Set au lieu d'Array)
  if (serialized.learnedWords instanceof Set) {

    return serialized as UserProgress;
  }

  // Supporter les deux formats de nommage
  const learnedWordsArray = serialized.learnedWords || serialized.learned_words || [];
  const wordsToReviewArray = serialized.wordsToReview || serialized.words_to_review || [];
  const recentlyLearnedWordsArray = serialized.recentlyLearnedWords || serialized.recently_learned_words || [];
  const wordProgressObj = serialized.wordProgress || serialized.word_progress || {};
  
  // ✅ PROTECTION: Ne logger qu'une fois toutes les 2 secondes (éviter spam console)
  const now = Date.now();
  const lastLog = (window as any).__lastDeserializeLog || 0;
  
  if (now - lastLog > 2000) {
  
    (window as any).__lastDeserializeLog = now;
  }

  return {
    learnedWords: new Set(learnedWordsArray),
    wordsToReview: new Set(wordsToReviewArray),
    wordProgress: wordProgressObj,
    recentlyLearnedWords: recentlyLearnedWordsArray,
    language: serialized.language || 'ba' as LanguageCode
  };
};

export default {};